import React, { useEffect, useMemo, useState } from "react";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { Checkbox } from "primereact/checkbox";
import { SelectButton } from "primereact/selectbutton";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import ResponsiveView from "../responsive/ResponsiveView";
import DataCard from "../responsive/DataCard";
import { formatMoney, paymentRowStatus } from "../../services/clientPaymentService";
import { CURRENCY_OPTIONS, fetchCurrentExchangeRate } from "../../services/exchangeRateService";
import styles from "./PaymentAllocationPanel.module.scss";

const ALLOCATION_MODE_OPTIONS = [
    { label: "Automatic", value: "automatic" },
    { label: "Selective", value: "selective" },
];

const computePreview = (invoices, totalAvailable) => {
    let remaining = totalAvailable;
    const rows = invoices.map((inv) => {
        const balanceBefore = inv.invoiceAmount || 0;
        if (remaining <= 0 || balanceBefore <= 0) {
            return { ...inv, amountApplied: 0, balanceAfter: balanceBefore, fullyPaid: false };
        }
        const amountApplied = Math.min(remaining, balanceBefore);
        const balanceAfter = Number((balanceBefore - amountApplied).toFixed(2));
        remaining = Number((remaining - amountApplied).toFixed(2));
        return { ...inv, amountApplied, balanceAfter, fullyPaid: balanceAfter <= 0.01 };
    });
    const creditAdded = remaining > 0 ? remaining : 0;
    return { rows, creditAdded };
};

// Shared allocation engine UI: given a fixed pool of outstanding invoices,
// lets staff either walk them oldest-first automatically or hand-pick exactly
// which invoices get how much, then hands the caller a validated snapshot to
// act on (record cash/wire immediately, or stage a card charge). Never
// computes the real allocation itself - that's always re-derived and
// re-validated server-side (controllers/clientPayment.js), this is only a
// preview so staff can see what will happen before committing.
const PaymentAllocationPanel = ({
    invoices,
    totalOutstanding,
    accountCreditBalance = 0,
    applyCredit,
    onApplyCreditChange,
    methodOptions,
    paymentMethod,
    onPaymentMethodChange,
    cashRegisterOptions,
    cashRegisterId,
    onCashRegisterIdChange,
    lockedCashRegisterLabel,
    actionLabel = "Confirm & Process Payment",
    actionIcon = "pi pi-check",
    onAction,
    submitting = false,
    actionDisabled = false,
    hideStats = false,
}) => {
    // A cash payment always needs a register attributed to it, but only
    // needs to ASK when the caller hasn't already got one to auto-attach
    // (RecordPayment skips the picker whenever the staff member recording
    // the payment already holds a register on their own active shift - see
    // that component for the exact rule). In-store pickup's use of this
    // panel never passes options either, since its cash entries already
    // attribute to the clerk's own active shift the same way.
    const requiresCashRegister = paymentMethod === "cash";
    const showCashRegisterPicker = !!cashRegisterOptions && requiresCashRegister;
    const [allocationMode, setAllocationMode] = useState("automatic");
    const [amountReceived, setAmountReceived] = useState(null);
    const [appliedAmount, setAppliedAmount] = useState(null);
    const [selectiveAmounts, setSelectiveAmounts] = useState({});
    // Wire/cash can be handed over in either currency - card/Amazon Pay never
    // route through this panel at all, so this toggle is always relevant
    // here. The exchange rate is fetched only to give staff an accurate
    // live preview before submitting; the server always re-converts and
    // re-validates for the amount actually applied (see processPayment).
    const [currency, setCurrency] = useState("usd");
    const [exchangeRate, setExchangeRate] = useState(null);
    const [rateLoaded, setRateLoaded] = useState(false);

    useEffect(() => {
        fetchCurrentExchangeRate().then((res) => {
            setExchangeRate(res?.data?.rate || null);
            setRateLoaded(true);
        });
    }, []);

    const toUsdEquivalent = (amount) => {
        if (currency !== "jmd") return Number(amount) || 0;
        if (!exchangeRate) return 0;
        return Number((Number(amount) / exchangeRate).toFixed(2));
    };
    const jmdBlocked = currency === "jmd" && rateLoaded && !exchangeRate;
    // Card/Amazon Pay never touch this toggle - they're USD-only by design
    // and never route through this panel's currency state (paymentMethod is
    // still passed through as "card" here so requiresCashRegister etc. stay
    // correct, it just can't be paired with a JMD amount).
    const showCurrencyToggle = paymentMethod !== "card";

    const handleAllocationModeChange = (value) => {
        if (!value) return;
        setAllocationMode(value);
        setSelectiveAmounts({});
    };

    // The amount received only ever changes what's available to allocate once
    // it's explicitly applied - typing in the field alone does nothing, so the
    // invoice table can't react to a half-typed or mistaken number.
    const handleApplyAmount = () => {
        if (!amountReceived || Number(amountReceived) <= 0) {
            return;
        }
        setAppliedAmount(Number(amountReceived));
        setSelectiveAmounts({});
    };

    const totalAvailable = toUsdEquivalent(appliedAmount) + (applyCredit ? accountCreditBalance : 0);
    const isAmountApplied = appliedAmount !== null && Number(amountReceived) === appliedAmount;

    const handleCurrencyChange = (value) => {
        setCurrency(value);
        // The USD-equivalent changes with the currency, so a previously
        // "Applied" amount needs re-confirming - same as editing the amount
        // itself does via isAmountApplied below.
        setAppliedAmount(null);
        setSelectiveAmounts({});
    };

    // How much of the total available is still unclaimed by OTHER selected
    // invoices - this is the real ceiling for any one row, not just that
    // invoice's own balance, otherwise two invoices could each be capped at
    // their own balance yet still sum to more than what was actually paid.
    const remainingBudgetExcluding = (invoiceId) => {
        const otherSelectedTotal = Object.entries(selectiveAmounts).reduce(
            (sum, [id, amt]) => (id === invoiceId ? sum : sum + (Number(amt) || 0)),
            0,
        );
        return Math.max(0, Number((totalAvailable - otherSelectedTotal).toFixed(2)));
    };

    const toggleInvoiceSelection = (invoice, checked) => {
        setSelectiveAmounts((prev) => {
            const next = { ...prev };
            if (checked) {
                next[invoice._id] = Math.min(invoice.invoiceAmount, remainingBudgetExcluding(invoice._id));
            } else {
                delete next[invoice._id];
            }
            return next;
        });
    };

    const updateSelectiveAmount = (invoice, amount) => {
        const cap = Math.min(invoice.invoiceAmount, remainingBudgetExcluding(invoice._id));
        const clamped = amount === null || amount === undefined ? null : Math.min(Math.max(amount, 0), cap);
        setSelectiveAmounts((prev) => ({ ...prev, [invoice._id]: clamped }));
    };

    // Fills invoices oldest-first up to the total available, same ordering as
    // automatic mode, so this can never over-allocate either.
    const selectAllFull = () => {
        let budget = totalAvailable;
        const next = {};
        invoices.forEach((inv) => {
            if (budget <= 0) return;
            const amt = Math.min(inv.invoiceAmount, budget);
            if (amt > 0) {
                next[inv._id] = amt;
                budget = Number((budget - amt).toFixed(2));
            }
        });
        setSelectiveAmounts(next);
    };

    const clearSelections = () => setSelectiveAmounts({});

    const preview = useMemo(() => computePreview(invoices, totalAvailable), [invoices, totalAvailable]);

    const selectiveRows = useMemo(
        () =>
            invoices.map((inv) => {
                const amountApplied = selectiveAmounts[inv._id] || 0;
                const balanceAfter = Number((inv.invoiceAmount - amountApplied).toFixed(2));
                return { ...inv, amountApplied, balanceAfter, fullyPaid: balanceAfter <= 0.01 && amountApplied > 0 };
            }),
        [invoices, selectiveAmounts],
    );
    const selectiveTotalApplied = useMemo(
        () => Object.values(selectiveAmounts).reduce((s, v) => s + (Number(v) || 0), 0),
        [selectiveAmounts],
    );
    const selectiveUnallocated = Number((totalAvailable - selectiveTotalApplied).toFixed(2));

    const canSubmit =
        !!appliedAmount &&
        appliedAmount > 0 &&
        isAmountApplied &&
        !jmdBlocked &&
        (!methodOptions || !!paymentMethod) &&
        (!requiresCashRegister || !!cashRegisterId) &&
        (allocationMode !== "selective" || selectiveTotalApplied > 0);

    const handleActionClick = () => {
        if (!appliedAmount || appliedAmount <= 0) return;
        if (!isAmountApplied) return;
        if (jmdBlocked) return;
        if (methodOptions && !paymentMethod) return;
        if (requiresCashRegister && !cashRegisterId) return;

        let selections = null;
        if (allocationMode === "selective") {
            selections = Object.entries(selectiveAmounts)
                .filter(([, amt]) => Number(amt) > 0)
                .map(([invoiceId, amount]) => ({ invoiceId, amount: Number(amount) }));
            if (selections.length === 0) return;
            if (selectiveTotalApplied > totalAvailable + 0.01) return;
        }

        if (onAction) {
            onAction({
                mode: allocationMode,
                amountReceived: appliedAmount,
                currency,
                totalAvailable,
                creditApplied: applyCredit ? accountCreditBalance : 0,
                selections: selections || undefined,
                previewRows: allocationMode === "selective" ? selectiveRows : preview.rows,
                creditAdded: allocationMode === "selective" ? Math.max(0, selectiveUnallocated) : preview.creditAdded,
                ...(requiresCashRegister ? { cashRegisterId } : {}),
            });
        }
    };

    const statusBody = (row) => {
        const { label, badgeClass } = paymentRowStatus(row);
        return <span className={`${styles.badge} ${styles[badgeClass]}`}>{label}</span>;
    };

    // Purchase invoices (goods bought for the client) get a chip, and read
    // "Item Purchase" until a linked package gives them a flight number.
    const invoiceCell = (row) => {
        const id = row.invoiceId || row.invoiceNo || "-";
        if (row.invoiceCategory !== "purchase") return id;
        return (
            <span className={styles.invoiceCell}>
                {id}
                <span className={styles.purchaseChip}>Purchase</span>
            </span>
        );
    };

    const flightCell = (row) => row.shipmentNumber || (row.invoiceCategory === "purchase" ? "Item Purchase" : "-");

    const previewCard = (row, i) => (
        <DataCard
            key={row._id || i}
            accent={row.fullyPaid ? "green" : row.amountApplied > 0 ? "amber" : "slate"}
            title={invoiceCell(row)}
            subtitle={row.Waybills}
            badge={statusBody(row)}
            fields={[
                { label: "Flight #", value: flightCell(row) },
                { label: "Owed", value: formatMoney(row.invoiceAmount) },
                { label: "Applied", value: formatMoney(row.amountApplied) },
                { label: "Remaining", value: formatMoney(row.balanceAfter) },
            ]}
        />
    );

    const selectiveCard = (row, i) => {
        const selected = row._id in selectiveAmounts;
        return (
            <DataCard
                key={row._id || i}
                accent={row.fullyPaid ? "green" : selected ? "amber" : "slate"}
                title={invoiceCell(row)}
                subtitle={row.Waybills}
                badge={
                    <Checkbox
                        checked={selected}
                        onChange={(e) => toggleInvoiceSelection(row, e.checked)}
                        disabled={!appliedAmount}
                        aria-label={`Select invoice ${row.invoiceId || row.invoiceNo || ""}`}
                    />
                }
                fields={[
                    { label: "Flight #", value: flightCell(row) },
                    { label: "Owed", value: formatMoney(row.invoiceAmount) },
                    {
                        label: "Apply",
                        value: (
                            <InputNumber
                                value={selectiveAmounts[row._id] ?? null}
                                onValueChange={(e) => updateSelectiveAmount(row, e.value)}
                                mode="currency"
                                currency="USD"
                                locale="en-US"
                                min={0}
                                max={row.invoiceAmount}
                                disabled={!selected}
                                placeholder="$0.00"
                                inputStyle={{ width: "120px", textAlign: "right" }}
                            />
                        ),
                    },
                    { label: "Result", value: statusBody(row) },
                ]}
            />
        );
    };

    return (
        <div className={styles.panel}>
            {!hideStats && accountCreditBalance > 0 && (
                <div className={styles.creditToggle}>
                    <Checkbox inputId="paPanelApplyCredit" checked={applyCredit} onChange={(e) => onApplyCreditChange?.(e.checked)} />
                    <label htmlFor="paPanelApplyCredit">
                        Apply this client's existing credit balance ({formatMoney(accountCreditBalance)}) toward this payment
                    </label>
                </div>
            )}

            <div className={styles.field}>
                <label>Allocation Mode</label>
                <SelectButton value={allocationMode} options={ALLOCATION_MODE_OPTIONS} onChange={(e) => handleAllocationModeChange(e.value)} />
                <span className={styles.modeHint}>
                    {allocationMode === "automatic"
                        ? "The amount is applied oldest-invoice-first automatically."
                        : "Choose exactly which invoices to pay, fully or partially."}
                </span>
            </div>

            <div className={styles.paymentForm}>
                {showCurrencyToggle && (
                    <div className={styles.field} style={{ marginBottom: 0 }}>
                        <label>Currency</label>
                        <SelectButton value={currency} options={CURRENCY_OPTIONS} onChange={(e) => e.value && handleCurrencyChange(e.value)} />
                    </div>
                )}
                <div className={styles.field} style={{ marginBottom: 0 }}>
                    <label>Amount Received{currency === "jmd" ? " (JMD)" : ""}</label>
                    <div className={styles.applyRow}>
                        <InputNumber
                            value={amountReceived}
                            onValueChange={(e) => setAmountReceived(e.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleApplyAmount()}
                            mode="currency"
                            currency={currency === "jmd" ? "JMD" : "USD"}
                            locale="en-US"
                            placeholder={currency === "jmd" ? "J$0.00" : "$0.00"}
                        />
                        <Button label="Apply" icon="pi pi-check" className={styles.actionButton} onClick={handleApplyAmount} />
                    </div>
                    {jmdBlocked ? (
                        <span className={styles.amountWarning}>No exchange rate has been set yet - an admin must set one (Cash &gt; Exchange Rate) before JMD can be accepted.</span>
                    ) : currency === "jmd" && exchangeRate && amountReceived ? (
                        <span className={styles.modeHint}>≈ {formatMoney(toUsdEquivalent(amountReceived))} USD at J${exchangeRate.toFixed(2)} per USD</span>
                    ) : !isAmountApplied ? (
                        <span className={styles.amountWarning}>
                            {appliedAmount === null ? "Enter an amount and click Apply to continue." : "Amount changed - click Apply again."}
                        </span>
                    ) : null}
                </div>
                {methodOptions && (
                    <div className={styles.field} style={{ marginBottom: 0 }}>
                        <label>Payment Method</label>
                        <Dropdown value={paymentMethod} options={methodOptions} onChange={(e) => onPaymentMethodChange?.(e.value)} placeholder="Select method" />
                    </div>
                )}
                {showCashRegisterPicker && (
                    <div className={styles.field} style={{ marginBottom: 0 }}>
                        <label>Cash Register</label>
                        <Dropdown
                            value={cashRegisterId}
                            options={cashRegisterOptions}
                            onChange={(e) => onCashRegisterIdChange?.(e.value)}
                            placeholder="Which register did this go into?"
                        />
                    </div>
                )}
                {requiresCashRegister && !showCashRegisterPicker && lockedCashRegisterLabel && (
                    <div className={styles.field} style={{ marginBottom: 0 }}>
                        <label>Cash Register</label>
                        <span className={styles.modeHint}>{lockedCashRegisterLabel} (your register for this shift)</span>
                    </div>
                )}
            </div>

            {totalAvailable > 0 && allocationMode === "automatic" && (
                <div className={styles.summaryBanner}>
                    <div className={styles.summaryItem}>
                        <div className={styles.summaryValue}>{formatMoney(totalAvailable)}</div>
                        <div className={styles.summaryLabel}>Total to Allocate</div>
                    </div>
                    <div className={styles.summaryItem}>
                        <div className={styles.summaryValue}>{formatMoney(preview.rows.reduce((s, r) => s + r.amountApplied, 0))}</div>
                        <div className={styles.summaryLabel}>Applied to Invoices</div>
                    </div>
                    <div className={styles.summaryItem}>
                        <div className={styles.summaryValue}>
                            {formatMoney(Math.max(0, (totalOutstanding ?? invoices.reduce((s, i) => s + (i.invoiceAmount || 0), 0)) - preview.rows.reduce((s, r) => s + r.amountApplied, 0)))}
                        </div>
                        <div className={styles.summaryLabel}>Remaining Outstanding</div>
                    </div>
                    {preview.creditAdded > 0 && (
                        <div className={styles.summaryItem}>
                            <div className={styles.summaryValue}>{formatMoney(preview.creditAdded)}</div>
                            <div className={styles.summaryLabel}>Overpayment &rarr; New Credit</div>
                        </div>
                    )}
                </div>
            )}

            {allocationMode === "selective" && (
                <div className={styles.summaryBanner}>
                    <div className={styles.summaryItem}>
                        <div className={styles.summaryValue}>{formatMoney(totalAvailable)}</div>
                        <div className={styles.summaryLabel}>Available</div>
                    </div>
                    <div className={styles.summaryItem}>
                        <div className={styles.summaryValue}>{formatMoney(selectiveTotalApplied)}</div>
                        <div className={styles.summaryLabel}>Allocated</div>
                    </div>
                    <div className={styles.summaryItem}>
                        <div className={styles.summaryValue} style={{ color: selectiveUnallocated < 0 ? "#dc2626" : undefined }}>
                            {selectiveUnallocated < 0 ? `Over by ${formatMoney(Math.abs(selectiveUnallocated))}` : formatMoney(selectiveUnallocated)}
                        </div>
                        <div className={styles.summaryLabel}>{selectiveUnallocated < 0 ? "Over-Allocated" : "Unallocated → Credit"}</div>
                    </div>
                </div>
            )}

            {allocationMode === "selective" && (
                <div className={styles.selectRow}>
                    <div>
                        <Button label="Select All (Full)" icon="pi pi-check-square" outlined onClick={selectAllFull} disabled={!appliedAmount} />
                        <Button label="Clear" icon="pi pi-times" className="p-button-text" style={{ marginLeft: "8px" }} onClick={clearSelections} />
                    </div>
                    {!appliedAmount && <span style={{ fontSize: "12px", color: "#94a3b8" }}>Apply an amount above before selecting invoices.</span>}
                </div>
            )}

            {allocationMode === "automatic" ? (
                <ResponsiveView value={preview.rows} emptyMessage="No invoices to allocate against." storageKey="paymentAllocationPanel.preview" renderCard={previewCard} flush>
                    <DataTable value={preview.rows} className="datatable-responsive custom-datatable">
                        <Column field="invoiceId" header="Invoice" body={(row) => invoiceCell(row)} />
                        <Column field="Waybills" header="Waybill" />
                        <Column field="shipmentNumber" header="Flight #" body={(row) => flightCell(row)} />
                        <Column header="Owed" body={(row) => formatMoney(row.invoiceAmount)} />
                        <Column header="Applied" body={(row) => formatMoney(row.amountApplied)} />
                        <Column header="Remaining" body={(row) => formatMoney(row.balanceAfter)} />
                        <Column header="Result" body={statusBody} />
                    </DataTable>
                </ResponsiveView>
            ) : (
                <ResponsiveView value={selectiveRows} emptyMessage="No invoices to allocate against." storageKey="paymentAllocationPanel.selective" renderCard={selectiveCard} flush>
                    <DataTable value={selectiveRows} className="datatable-responsive custom-datatable" style={{ minWidth: "900px" }}>
                        <Column
                            header=""
                            style={{ width: "44px" }}
                            body={(row) => <Checkbox checked={row._id in selectiveAmounts} onChange={(e) => toggleInvoiceSelection(row, e.checked)} disabled={!appliedAmount} />}
                        />
                        <Column field="invoiceId" header="Invoice" style={{ width: "120px" }} body={(row) => invoiceCell(row)} />
                        <Column field="Waybills" header="Waybill" style={{ width: "130px" }} />
                        <Column field="shipmentNumber" header="Flight #" style={{ width: "100px" }} body={(row) => flightCell(row)} />
                        <Column header="Owed" style={{ width: "110px" }} body={(row) => formatMoney(row.invoiceAmount)} />
                        <Column
                            header="Amount to Apply"
                            style={{ width: "170px" }}
                            body={(row) => (
                                <InputNumber
                                    value={selectiveAmounts[row._id] ?? null}
                                    onValueChange={(e) => updateSelectiveAmount(row, e.value)}
                                    mode="currency"
                                    currency="USD"
                                    locale="en-US"
                                    min={0}
                                    max={row.invoiceAmount}
                                    disabled={!(row._id in selectiveAmounts)}
                                    placeholder="$0.00"
                                    inputStyle={{ width: "130px" }}
                                />
                            )}
                        />
                        <Column header="Result" style={{ width: "210px" }} body={statusBody} />
                    </DataTable>
                </ResponsiveView>
            )}

            <div className={styles.actionRow}>
                <Button
                    label={submitting ? "Processing..." : actionLabel}
                    icon={submitting ? "pi pi-spin pi-spinner" : actionIcon}
                    className={styles.actionButton}
                    onClick={handleActionClick}
                    disabled={submitting || actionDisabled || !canSubmit}
                />
            </div>
        </div>
    );
};

export default PaymentAllocationPanel;
